// ────────────────────────────────────────────────────────────────────
// The queue's one list, and the one filter over it.
//
// ONE LIST. `on_you` and `moving_elsewhere` are not two sources — they are the
// same rows split by `mine`, which the server decided. So this module
// concatenates them back into the single list the design describes, and every
// other thing on the screen (the KPI row, the flow rail, the blocked rail) is
// a view of that one list. The screen that shipped on 2026-08-31 rendered
// three stacked sections from three endpoints with chips that filtered only
// the first; a filter that narrows one section and leaves the rest alone is
// that same defect wearing a different layout, and rebuilding the list here
// is what makes it structurally impossible.
//
// WHY THERE IS ANY COUNTING AT ALL IN THE BROWSER. The rule for this screen is
// that the server computes and the browser prints — every chip, sentence and
// label on a row arrives finished. Counting is the one deliberate exception,
// and only over rows the USER has filtered down to. `QueueCounts` is counted
// off the full row set server-side; the moment a filter hides rows, that count
// no longer describes the list under it, and "a count that can disagree with
// its own list is lying" is the whole reason the count exists. So the KPI row
// is counted off exactly the rows rendered beneath it — the same arithmetic
// the server performed, over the subset the user chose. `queueSummary` over an
// unfiltered list is asserted to equal the server's own counts, so the wire
// stays the authority and this cannot silently drift from it.
// ────────────────────────────────────────────────────────────────────
import type { QueueOut, QueueRow } from "./types";

/** The sidebar's active narrowing. One at a time, and clicking the active one
 *  clears it — "Click a stage or a client to narrow the queue. Click it again
 *  to clear." */
export type QueueFilter =
  | { kind: "stage"; key: string }
  | { kind: "client"; key: string };

/** A client's identity as a filter key. A job with no plant yet legitimately
 *  has no client; that is its own bucket, never folded into another client's. */
export function clientKey(clientId: number | null | undefined): string {
  return clientId === null || clientId === undefined ? "none" : String(clientId);
}

export function sameFilter(a: QueueFilter | null, b: QueueFilter | null): boolean {
  if (a === null || b === null) return a === b;
  return a.kind === b.kind && a.key === b.key;
}

/** Click semantics: the same filter again clears; a different one replaces. */
export function toggleFilter(
  current: QueueFilter | null, next: QueueFilter,
): QueueFilter | null {
  return sameFilter(current, next) ? null : next;
}

/** The single list, in the server's order. `on_you` first is not a re-sort:
 *  the server sorted the whole set by what is blocking someone else and split
 *  it afterwards, so concatenating restores one list whose two halves each
 *  keep that order. */
export function allRows(data: QueueOut): QueueRow[] {
  return [...data.on_you, ...data.moving_elsewhere];
}

export function matchesFilter(row: QueueRow, filter: QueueFilter | null): boolean {
  if (filter === null) return true;
  if (filter.kind === "stage") return row.stage === filter.key;
  return clientKey(row.client_id) === filter.key;
}

/** The WHOLE list narrows — never one section of it. */
export function applyFilter(rows: QueueRow[], filter: QueueFilter | null): QueueRow[] {
  return filter === null ? rows : rows.filter((r) => matchesFilter(r, filter));
}

export interface QueueSummary {
  total: number;
  onYou: number;
  movingElsewhere: number;
  blocked: number;
  overdue: number;
}

/** Counted off the rows handed in — the only way a figure and its list cannot
 *  disagree. Mirrors server/routers/queue.py::_counts field for field. */
export function queueSummary(rows: QueueRow[]): QueueSummary {
  return {
    total: rows.length,
    onYou: rows.filter((r) => r.mine).length,
    movingElsewhere: rows.filter((r) => !r.mine).length,
    blocked: rows.filter((r) => r.blocked).length,
    overdue: rows.filter((r) => r.overdue).length,
  };
}

export interface FlowStage {
  stage: string;
  label: string;
  /** 1-based place in the pipeline, from the wire. Never the array position:
   *  numbering a list by where things happen to land in it is the browser
   *  asserting an order the server did not claim. */
  index: number;
  /** The owning role, from the server's one stage→role map — never
   *  re-derived from a table copied into the browser, and present even for a
   *  stage that currently holds nothing. */
  ownerRole: string;
  count: number;
}

/** The whole pipeline as the response describes it: `counts.by_stage` supplies
 *  the order, the number, the label and the owning role; the visible rows
 *  supply the count.
 *
 *  EVERY stage is here, the empty ones included — `1 Intake … 6 Deliver`, as
 *  the design draws it. A stage standing empty is information: it says the
 *  pipeline is clear there, and dropping it made "nothing is here"
 *  indistinguishable from "this stage does not exist". */
export function flowStages(data: QueueOut, visible: QueueRow[]): FlowStage[] {
  const counted = new Map<string, number>();
  for (const r of visible) counted.set(r.stage, (counted.get(r.stage) ?? 0) + 1);
  return data.counts.by_stage.map((s) => ({
    stage: s.stage, label: s.label, index: s.index, ownerRole: s.owner_role,
    count: counted.get(s.stage) ?? 0,
  }));
}
